import { SUPPLIER_MATERIAL_FIELDS } from '@cutr/constants/material.ts';
import {
  MATERIAL_SUPPLIERS,
  MaterialSupplierCode,
} from '@cutr/constants/suppliers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  agentApi,
  MaterialDiffItem,
  MaterialDiffUpdate,
  MaterialsImportPreview,
} from '@/api/backend/agent';
import { Button } from '@/primitives/Button';
import { Download, Icon, Upload } from '@/primitives/Icons';
import { useTicker } from '@/theme';
import { saveBlob } from '@/utils/file';

import styles from './CutlistSettings.module.css';

const TIMMERPANEEL_SAMPLE_CSV = [
  'article_code;length_mm;width_mm;thickness_um;price_amount;price_unit;Stock;Brand;Type;short_description;long_description;weigth_mg;weigth_unit;sawing_allowed;has_grain_direction;is_active',
  '20288002000;2000;800;28000;85,08;pcs;0;;solid-wood;Grenen paneel 28mm 80x200cm;Grenen paneel 28mm 80x200cm;20610000;pcs;1;1;1',
  '10183003000;3000;300;18000;25,75;pcs;0;;solid-wood;Vuren paneel 18mm 30x300cm;Vuren paneel 18mm 30x300cm;6880000;pcs;0;1;1',
  '',
].join('\n');

const SOURCE_SAMPLE_CSV: Partial<Record<string, string>> = {
  TIMMERPANEEL: TIMMERPANEEL_SAMPLE_CSV,
  'LEEUWERIK-DEMO': TIMMERPANEEL_SAMPLE_CSV,
};

const FIELD_ORDER = Object.keys(SUPPLIER_MATERIAL_FIELDS);

// Internal/constant columns that are noise for the agent (DB id, the supplier
// code which is always their own source).
const HIDDEN_COLUMNS = new Set(['id', 'supplierCode']);

// The columns each source's parser actually reads from the uploaded file, in
// display order. A value-based heuristic can't be used here: parsers write
// constant defaults (e.g. `isPrimed: false`) for canonical fields the file
// doesn't contain, so those would otherwise show as always-empty noise columns.
// Keep TIMMERPANEEL in sync with server .../parsers/timmerpaneel/parser.js.
const TIMMERPANEEL_COLUMNS = [
  'articleCode',
  'name',
  'type',
  'lengthMM',
  'widthMM',
  'thicknessUM',
  'priceAmount',
  'priceUnit',
  'priceCurrency',
  'weightMG',
  'weightUnit',
  'brand',
  'shortDescription',
  'longDescription',
  'sawingAllowed',
  'hasGrainDirection',
  'isActive',
];

const SOURCE_COLUMNS: Partial<Record<string, string[]>> = {
  TIMMERPANEEL: TIMMERPANEEL_COLUMNS,
  'LEEUWERIK-DEMO': TIMMERPANEEL_COLUMNS,
};

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '';

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Columns relevant to the agent: the source's declared parser columns when we
// have them, otherwise any canonical field carrying a value. Either way, drop
// columns that are empty across every row.
const relevantColumns = (
  source: string,
  rows: MaterialDiffItem[]
): string[] => {
  const candidates =
    SOURCE_COLUMNS[source] ??
    FIELD_ORDER.filter((field) => !HIDDEN_COLUMNS.has(field));
  return candidates.filter((field) => rows.some((row) => hasValue(row[field])));
};

export const CutlistSettings = () => {
  const { t } = useTranslation();
  const source = useTicker() as MaterialSupplierCode;
  const supplierName = MATERIAL_SUPPLIERS[source] ?? source;
  const accept = source === 'SWISSKRONO' ? '.xlsx' : '.csv';
  const sampleCsv = SOURCE_SAMPLE_CSV[source];

  const [isDragOver, setIsDragOver] = React.useState(false);
  const [progress, setProgress] = React.useState<number | null>(null);
  const [preview, setPreview] = React.useState<MaterialsImportPreview | null>(
    null
  );
  const [priceConsent, setPriceConsent] = React.useState(false);
  const [isApplying, setIsApplying] = React.useState(false);

  const handleFile = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setPreview(null);
    setPriceConsent(false);
    setProgress(0);
    agentApi
      .uploadMaterialsImport(file, setProgress)
      .then((data) => {
        setPreview(data);
        setProgress(null);
      })
      .catch((err: Error) => {
        setProgress(null);
        toast.error(err.message || t('agent.settings.uploadError'));
      });
  };

  const handleApply = () => {
    if (!preview) return;
    setIsApplying(true);
    agentApi
      .applyMaterialsImport(preview.s3Reference)
      .then(() => {
        toast.success(t('agent.settings.applied'));
        setPreview(null);
      })
      .catch(() => toast.error(t('agent.settings.applyError')))
      .finally(() => setIsApplying(false));
  };

  const handleCancel = () => {
    setPreview(null);
    setPriceConsent(false);
  };

  const handleDownloadSample = () => {
    if (!sampleCsv) return;
    saveBlob(
      new Blob([sampleCsv], { type: 'text/csv;charset=utf-8;' }),
      `${source.toLowerCase()}-materials-sample.csv`
    );
  };

  const canApply =
    !isApplying && (!preview?.priceChangeWarning || priceConsent);

  return (
    <div className={styles.page}>
      <h1 className={styles.header}>{t('agent.settings.title')}</h1>
      <p className={styles.supplier}>
        {t('agent.settings.supplierLabel', { supplier: supplierName })}
      </p>
      <p className={styles.warning}>
        {t('agent.settings.description', {
          source: supplierName.toUpperCase().charAt(0) + supplierName.slice(1),
        })}
      </p>

      {!preview && sampleCsv && (
        <div className={styles.sampleRow}>
          <Button
            variant="secondary"
            icon={<Icon icon={<Download />} />}
            onClick={handleDownloadSample}
          >
            {t('agent.settings.downloadSample')}
          </Button>
        </div>
      )}

      {!preview && (
        <label
          className={`${styles.uploadArea} ${
            isDragOver ? styles.uploadAreaDragOver : ''
          }`}
          htmlFor="materials-upload"
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            handleFile(e.dataTransfer.files);
          }}
        >
          <Icon icon={<Upload />} size={8} className={styles.uploadIcon} />
          <span className={styles.uploadText}>
            <strong>{t('agent.settings.uploadCta')}</strong>
          </span>
          <span className={styles.uploadFormats}>
            {t('agent.settings.uploadFormats', { formats: accept })}
          </span>
          <input
            id="materials-upload"
            type="file"
            className={styles.hiddenInput}
            accept={accept}
            disabled={progress !== null}
            onChange={(e) => {
              handleFile(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      )}

      {progress !== null && (
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {preview && (
        <div className={styles.preview}>
          {preview.summaryLines?.length > 0 && (
            <div>
              <h2 className={styles.sectionTitle}>
                {t('agent.settings.summary')}
              </h2>
              <ul className={styles.summaryList}>
                {preview.summaryLines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          <DiffTable
            source={source}
            title={t('agent.settings.itemsToCreate', {
              count: preview.itemsToCreate.length,
            })}
            items={preview.itemsToCreate}
            rowClass={styles.rowNew}
            show={preview.itemsToCreate.length > 0}
          />
          <DiffTable
            source={source}
            title={t('agent.settings.itemsToDelete', {
              count: preview.itemsToDelete.length,
            })}
            items={preview.itemsToDelete}
            rowClass={styles.rowOld}
            strike
            show={preview.itemsToDelete.length > 0}
          />
          <DiffUpdateTable
            source={source}
            title={t('agent.settings.itemsToUpdate', {
              count: preview.itemsToUpdate.length,
            })}
            updates={preview.itemsToUpdate}
            show={preview.itemsToUpdate.length > 0}
          />
          <DiffUpdateTable
            source={source}
            title={t('agent.settings.itemsToRestore', {
              count: preview.itemsToRestore.length,
            })}
            updates={preview.itemsToRestore}
            show={preview.itemsToRestore.length > 0}
          />

          {preview.priceChangeWarning && (
            <div className={styles.priceWarning}>
              <p>{preview.priceChangeWarning}</p>
              <label className={styles.consent}>
                <input
                  type="checkbox"
                  checked={priceConsent}
                  onChange={(e) => setPriceConsent(e.target.checked)}
                />
                {t('agent.settings.priceChangeConsent')}
              </label>
            </div>
          )}

          <div className={styles.actions}>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isApplying}
            >
              {t('agent.settings.cancel')}
            </Button>
            <Button onClick={handleApply} disabled={!canApply}>
              {t('agent.settings.approve')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface DiffTableProps {
  source: string;
  title: string;
  items: MaterialDiffItem[];
  rowClass: string;
  strike?: boolean;
  show: boolean;
}

const DiffTable = ({
  source,
  title,
  items,
  rowClass,
  strike,
  show,
}: DiffTableProps) => {
  if (!show) return null;
  const columns = relevantColumns(source, items);
  return (
    <div className={styles.diffSection}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((field) => (
                <th key={field} className={styles.th}>
                  {SUPPLIER_MATERIAL_FIELDS[
                    field as keyof typeof SUPPLIER_MATERIAL_FIELDS
                  ] ?? field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                {columns.map((field) => (
                  <td key={field} className={rowClass}>
                    {strike ? (
                      <s>{formatCell(item[field])}</s>
                    ) : (
                      formatCell(item[field])
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface DiffUpdateTableProps {
  source: string;
  title: string;
  updates: MaterialDiffUpdate[];
  show: boolean;
}

const DiffUpdateTable = ({
  source,
  title,
  updates,
  show,
}: DiffUpdateTableProps) => {
  if (!show) return null;
  // Consider both the existing values and the incoming changes so a field being
  // set from empty to a value still gets a column.
  const columns = relevantColumns(
    source,
    updates.map((u) => ({ ...u.item, ...u.valuesToUpdate }))
  );
  return (
    <div className={styles.diffSection}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((field) => (
                <th key={field} className={styles.th}>
                  {SUPPLIER_MATERIAL_FIELDS[
                    field as keyof typeof SUPPLIER_MATERIAL_FIELDS
                  ] ?? field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {updates.map((entry, i) => (
              <tr key={i}>
                {columns.map((field) => {
                  const changed = field in entry.valuesToUpdate;
                  return (
                    <td
                      key={field}
                      className={changed ? styles.rowNew : undefined}
                    >
                      {changed ? (
                        <span>
                          <s className={styles.rowOld}>
                            {formatCell(entry.item[field])}
                          </s>{' '}
                          {formatCell(entry.valuesToUpdate[field])}
                        </span>
                      ) : (
                        formatCell(entry.item[field])
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
